import React, { useState, useCallback, useMemo } from 'react';
import { Plus, Search, Users, X } from 'lucide-react';
import AppLoader from '../components/modules/AppLoader';
import ModuleAlert from '../components/modules/ModuleAlert';
import ModulePageHeader from '../components/modules/ModulePageHeader';
import { getThemeColor } from '../config/appConfig';
import apiService from '../utils/apiService';
import { useFetchOnceByKey } from '../hooks/useMountOnce';
import '../styles/module-shared.css';
import './subPartners.css';

const THEME = getThemeColor('primary');

const pick = (obj, ...keys) => {
  for (const key of keys) {
    if (obj?.[key] != null && obj[key] !== '') return obj[key];
  }
  return undefined;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const mapSubPartner = (item, index) => {
  const isActiveRaw = pick(item, 'isActive', 'IsActive');
  const isActive =
    isActiveRaw === true ||
    isActiveRaw === 1 ||
    String(isActiveRaw).toLowerCase() === 'true';

  return {
    id: pick(item, 'affiliateUserId', 'AffiliateUserId', 'id') ?? index,
    email: String(pick(item, 'email', 'Email') || '—'),
    affiliateCode: String(pick(item, 'affiliateCode', 'AffiliateCode') || '—'),
    createdDateTime:
      pick(item, 'createdDateTime', 'CreatedDateTime', 'createdDate', 'CreatedDate') || null,
    isActive,
    totalClients: Number(pick(item, 'totalClients', 'TotalClients') ?? 0),
  };
};

const Affiliates = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newAffiliateEmail, setNewAffiliateEmail] = useState('');
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [partners, setPartners] = useState([]);

  const fetchSubPartners = useCallback(async ({ cancelled }) => {
    try {
      setListLoading(true);
      setListError('');
      const response = await apiService.getSubPartnersList();
      if (cancelled()) return;
      if (!response.success) {
        setPartners([]);
        setListError(response.error || 'Failed to load sub partners');
        return;
      }
      const raw = response?.data?.result ?? response?.data ?? response?.result ?? [];
      const list = Array.isArray(raw) ? raw : [];
      setPartners(list.map(mapSubPartner));
    } catch (err) {
      if (!cancelled()) {
        setPartners([]);
        setListError(err.message || 'Failed to load sub partners');
      }
    } finally {
      if (!cancelled()) setListLoading(false);
    }
  }, []);

  useFetchOnceByKey('sub-partners-list-v3', fetchSubPartners, true);

  const filteredPartners = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return partners.filter((row) => {
      const matchesSearch =
        !q ||
        row.email.toLowerCase().includes(q) ||
        row.affiliateCode.toLowerCase().includes(q);
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && row.isActive) ||
        (statusFilter === 'inactive' && !row.isActive);
      return matchesSearch && matchesStatus;
    });
  }, [partners, searchTerm, statusFilter]);

  const handleAddAffiliate = () => {
    if (!newAffiliateEmail.trim()) return;
    alert('Partner invitation is not available yet. Please contact support.');
    setShowAddModal(false);
    setNewAffiliateEmail('');
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setNewAffiliateEmail('');
  };

  return (
    <div className="page sp-page">
      <div className="page-inner">
        <div className="sp-header-row">
          <ModulePageHeader
            icon={Users}
            title="Sub Partner Listing"
            subtitle="Manage your sub partners and their performance"
            iconBg={`linear-gradient(135deg,${THEME},#a52a2a)`}
          />
          <button type="button" className="sp-btn sp-btn--primary" onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4" />
            Add Sub Partner
          </button>
        </div>

        <ModuleAlert message={listError} />

        <section className="module-card sp-toolbar">
          <div className="sp-toolbar__row">
            <div className="sp-search">
              <Search className="w-4 h-4" />
              <input
                type="text"
                placeholder="Search by email or partner code…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              className="sp-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </section>

        <section className="module-card sp-table-card">
          {listLoading ? (
            <div className="sp-loader">
              <AppLoader color={THEME} />
            </div>
          ) : (
            <div className="sp-table-wrap">
              <table className="sp-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Partner Code</th>
                    <th>Total Clients</th>
                    <th>Created Date Time</th>
                    <th>Is Active</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPartners.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="sp-empty">
                        No partners found
                      </td>
                    </tr>
                  ) : (
                    filteredPartners.map((row) => (
                      <tr key={row.id}>
                        <td className="sp-email" data-label="Email">{row.email}</td>
                        <td data-label="Partner Code">{row.affiliateCode}</td>
                        <td data-label="Total Clients">{Number.isFinite(row.totalClients) ? row.totalClients : 0}</td>
                        <td className="sp-muted" data-label="Created Date Time">{formatDateTime(row.createdDateTime)}</td>
                        <td data-label="Is Active">
                          <span className={`sp-pill ${row.isActive ? 'is-active' : 'is-inactive'}`}>
                            {row.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <p className="sp-footer">
          Showing <strong>{filteredPartners.length ? 1 : 0}</strong> to{' '}
          <strong>{filteredPartners.length}</strong> of <strong>{filteredPartners.length}</strong>{' '}
          results
        </p>
      </div>

      {showAddModal ? (
        <div className="sp-modal-backdrop" role="presentation" onClick={handleCloseModal}>
          <div
            className="sp-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sp-add-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sp-modal__head">
              <h3 id="sp-add-title">Add New Partner</h3>
              <button type="button" className="sp-icon-btn" onClick={handleCloseModal} aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="sp-modal__body">
              <label htmlFor="sp-email">Email Address</label>
              <input
                id="sp-email"
                type="email"
                value={newAffiliateEmail}
                onChange={(e) => setNewAffiliateEmail(e.target.value)}
                placeholder="Enter partner email address"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddAffiliate();
                }}
              />
              <p>
                An invitation will be sent to this email address. The partner will be able to join
                your program after accepting the invitation.
              </p>
            </div>
            <div className="sp-modal__foot">
              <button type="button" className="sp-btn sp-btn--ghost" onClick={handleCloseModal}>
                Cancel
              </button>
              <button
                type="button"
                className="sp-btn sp-btn--primary"
                disabled={!newAffiliateEmail.trim()}
                onClick={handleAddAffiliate}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Affiliates;
